/**
 * English app entry point.
 *
 * Boots:
 *   - StorageService + SrsSignals (defined in ./srs.js, re-exported here)
 *   - AudioCache + TTSClient + AudioService (en-US default voice)
 *   - QuizRegistry with 6 universal quiz modes (Multiple Choice,
 *     Listening First, Mixed Challenge, Speed Challenge, Typed Answer,
 *     Word Bank). English-specific modes register in task #11.
 *   - initStreak() + markActiveToday() on boot
 *   - Level-load watcher: when signals.currentLevel changes, async-loads
 *     that level's deck via dynamic import
 */

import { render } from 'preact';
import { effect } from '@preact/signals';
import {
  QuizRegistry,
  createMultipleChoice,
  createListeningFirst,
  createMixedChallenge,
  createSpeedChallenge,
  createTypedAnswer,
  createWordBank
} from '@kevvykevwin/lang-core/quiz';
import {
  AudioCache,
  AudioService,
  TTSClient
} from '@kevvykevwin/lang-core/audio';

import { ENGLISH_LEVELS, type EnglishCardExt, type EnglishLevel } from './types.js';
import { initStreak, markActiveToday } from './streak.js';
import { loadDeck } from './data/index.js';
import { learnerLanguage } from './i18n/learner.js';
import { signals, storage } from './srs.js';

export { signals, storage } from './srs.js';
import { phrasalVerbs } from './data/phrasalVerbs.js';
import distractorIndexData from './data/distractorIndex.json' with { type: 'json' };
import { createPhrasalVerbQuiz } from './quiz/PhrasalVerbQuiz.js';
import { createIrregularVerbQuiz } from './quiz/IrregularVerbQuiz.js';
import { createCollocationQuiz, type DistractorIndex } from './quiz/CollocationQuiz.js';
import { quizL1Subtext } from './quiz/quizL1Subtext.js';
import { bootSituations } from './situations/boot.js';
import { withSuppressedPush } from './state/syncSignals.js';
import { cloudSyncEnabled } from './featureFlags.js';
import { App } from './App.js';
import './index.css';

const distractorIndex: DistractorIndex = distractorIndexData as DistractorIndex;

// Storage and SRS signals live in ./srs.js (see top-of-file imports) so
// render-time helpers can read `signals` without pulling main's full app
// boot graph into unit tests.

// ============================================
// Situations boot — second SrsSignals instance + section state
// ============================================
//
// Called as a function (not a side-effect import) to avoid a circular
// init dependency: boot.ts needs `signals` to be defined when its
// effects register, but ESM hoisting would otherwise run boot.ts before
// `signals = createSrsSignals(...)` evaluates.

export const {
  currentSection,
  situationsSignals,
  activeSituationCategory
} = bootSituations(signals);

// ============================================
// Audio
// ============================================

export const audioCache = new AudioCache({
  indexedDBName: 'english-audio-cache',
  indexedDBVersion: 1
});

export const ttsClient = new TTSClient({
  defaultVoice: 'en-US-Polyglot-1',
  languageCode: 'en-US',
  endpoint: '/.netlify/functions/tts'
});

export const audioService = new AudioService({
  ttsClient,
  cache: audioCache
});

// ============================================
// Quiz Registry
// ============================================

export const quizRegistry = new QuizRegistry<EnglishCardExt>();

const getAllCards = () => signals.deck.value;

// JP option subtext is wired ONLY to multiple-choice / listening-first /
// mixed-challenge because their options ARE card.back definitions (where a
// JP gloss scaffolds reading at A1/A2/B1). SpeedChallenge uses the same
// option shape but lang-core does not yet expose `renderOptionSubtext` for
// it — tracked as a fast-follow. TypedAnswer / WordBank have no multi-option
// surface. Custom modes (Phrasal/Irregular/Collocation) display particles,
// verb forms, or collocates where a JP word gloss would be semantically
// wrong, so they are intentionally excluded.
quizRegistry.register(
  createMultipleChoice<EnglishCardExt>({
    signals,
    getAllCards,
    renderOptionSubtext: quizL1Subtext
  })
);
quizRegistry.register(
  createListeningFirst<EnglishCardExt>({
    signals,
    getAllCards,
    renderOptionSubtext: quizL1Subtext,
    onPlayAudio: (text: string) => {
      audioService.playAudio(text).catch(err => {
        console.warn('Audio playback failed:', err);
      });
    }
  })
);
quizRegistry.register(
  createMixedChallenge<EnglishCardExt>({
    signals,
    getAllCards,
    renderOptionSubtext: quizL1Subtext
  })
);
quizRegistry.register(createSpeedChallenge<EnglishCardExt>({ signals, getAllCards }));
quizRegistry.register(
  createTypedAnswer<EnglishCardExt>({
    signals,
    getAllCards,
    minCorrectStreak: 2,
    promptLabel: 'Type the meaning.'
  })
);
quizRegistry.register(
  createWordBank<EnglishCardExt>({
    signals,
    getAllCards,
    promptLabel: 'Tap the words in order.'
  })
);

// English-specific modes
quizRegistry.register(
  createPhrasalVerbQuiz({
    signals,
    getPhrasalVerbs: () => phrasalVerbs
  })
);
quizRegistry.register(
  createIrregularVerbQuiz({
    signals,
    getAllCards
  })
);
quizRegistry.register(
  createCollocationQuiz({
    signals,
    distractorIndex
  })
);

// ============================================
// Streak
// ============================================

initStreak();
// Mark active today on boot. If user comes back same-day, no-op; new day
// increments streak (or resets if they missed a day).
markActiveToday();

// ============================================
// Level-load wiring
// ============================================
//
// Watch currentLevel and load the corresponding deck. Replaces JLPT's
// App.tsx level-load useEffect — using @preact/signals `effect` directly
// avoids re-introducing useEffect into the React tree.

function isEnglishLevel(s: string): s is EnglishLevel {
  return (ENGLISH_LEVELS as readonly string[]).includes(s);
}

let deckLoadRequestId = 0;

effect(() => {
  const raw = signals.currentLevel.value;
  const level: EnglishLevel = isEnglishLevel(raw) ? raw : 'A1';
  // Subscribe to learnerLanguage so a language switch also re-runs loadDeck —
  // the captured language chooses which sidecar loadDeck should merge.
  // (A separate setLevel(currentLevel) effect would be a no-op because Preact
  // signals short-circuit on Object.is equality.)
  const lang = learnerLanguage.value;
  const requestId = ++deckLoadRequestId;
  loadDeck(level, lang)
    .then(cards => {
      if (
        requestId !== deckLoadRequestId ||
        signals.currentLevel.value !== level ||
        learnerLanguage.value !== lang
      ) {
        return;
      }
      // Initialize per-card progress entries for any new cards before
      // rebuilding the due deck (so SRS counters are accurate).
      const progress = storage.initializeWordProgress<EnglishCardExt>(level, cards);
      // Suppress cloud push: this is a programmatic level-load hydration, not a
      // user answer. Without this guard the SyncService push effect would fire
      // on every level switch / boot and push a freshly-hydrated map.
      withSuppressedPush(() => {
        signals.setWordProgressMap(progress);
        signals.setDeck(cards);
      });
    })
    .catch(err => {
      console.error('Failed to load deck for', level, err);
      signals.showNotification(`Could not load ${level} deck`, 'error');
    });
});

// Multi-tab storage sync: re-hydrate progress when another tab writes
// to the active level's key. Suppress cloud push — the originating tab (or
// SyncService) already owns the push for this write; re-pushing here would
// echo. Wrapped in withSuppressedPush so the SyncService effect ignores it.
window.addEventListener('storage', e => {
  const activeKey = `english-word-progress-${signals.currentLevel.value}`;
  if (e.key === activeKey) {
    withSuppressedPush(() => {
      signals.hydrateProgressForLevel(signals.currentLevel.value);
      signals.rebuildDueDeck();
    });
  }
});

// ============================================
// Cloud sync boot (lazy)
// ============================================
//
// Deferred to idle + dynamic-imported so SyncService → cloudStore →
// @supabase/supabase-js stays out of the initial bundle. Self-disables when
// the feature flag is off or env vars are missing (initSync returns null).
// After init, pull-merge the active level when the tab regains
// focus/visibility (guarded inside maybePullActiveLevel by a 30s min-interval
// + in-flight lock).
if (cloudSyncEnabled()) {
  const scheduleIdle = (cb: () => void): void => {
    const ric = window.requestIdleCallback;
    if (typeof ric === 'function') ric(() => cb());
    else window.setTimeout(cb, 1);
  };
  scheduleIdle(() => {
    void import('./services/SyncService.js').then(({ initSync, getSyncService }) => {
      void initSync();
      const pull = (): void => {
        void getSyncService()?.maybePullActiveLevel();
      };
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') pull();
      });
      window.addEventListener('focus', pull);
    });
  });
}

// ============================================
// Mount
// ============================================

const root = document.getElementById('root');
if (!root) {
  throw new Error('No #root element found in index.html');
}

render(<App />, root);
